/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : OrderSlice.ts
 *
 * Project Title : RDR7000
 *
 * Description   : Redux Slice for Order related state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : Amol Jagtap (H409453)
 *
 * @Date         : Aug 12, 2020
 *
 * @Version      : Version 1.0
 *
 * By            : Amol Jagtap (H409453)
 * On            : Aug 12, 2020
 * Description   : Add code to maintain state for order search and order details
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, RootState } from 'stores/GlobalStore';
import { apiCallBegan } from 'stores/middlewares';
import { IOrderState, IOrderFields, IOrderDetailFields } from 'models/orderSearch';

interface ISetSelectedSalesOrderPayload {
  orderNumber: string;
}

const initialState: IOrderState = {
  orderList: null,
  orderDetails: null,
  selectedOrder: null,
  orderLoader: false,
  selectedSalesOrder: '',
  orderDetailLoader: true,
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    setOrderList(orderState: IOrderState, action: PayloadAction<IOrderFields[]>) {
      orderState.orderList = action.payload;
      orderState.orderLoader = false;
    },
    beginOrderLoading(orderState: IOrderState) {
      orderState.orderLoader = true;
    },
    resetOrderListOnError(orderState: IOrderState) {
      orderState.orderList = [];
      orderState.orderLoader = false;
    },
    setSelectedSalesOrder(orderState: IOrderState, action: PayloadAction<ISetSelectedSalesOrderPayload>) {
      orderState.selectedSalesOrder = action.payload.orderNumber;
    },
    setOrderDetails(orderState: IOrderState, action: PayloadAction<IOrderDetailFields[]>) {
      orderState.orderDetails = action.payload;
      orderState.orderDetailLoader = false;
    },
    setSelectedOrder(orderState: IOrderState, action: PayloadAction<IOrderFields>) {
      orderState.selectedOrder = action.payload;
    },
    beginOrderDetailLoading(orderState: IOrderState) {
      orderState.orderDetailLoader = true;
    },
    stopOrderDetailLoading(orderState: IOrderState) {
      orderState.orderDetailLoader = false;
    },
  },
});

const {
  setOrderList,
  setOrderDetails,
  beginOrderLoading,
  resetOrderListOnError,
  beginOrderDetailLoading,
  stopOrderDetailLoading,
} = orderSlice.actions;
export const { setSelectedOrder } = orderSlice.actions;

export const orderReducer = orderSlice.reducer;

export const fetchOrders = (): AppThunk => async (dispatch, getState) => {
  const soldTo = getState().customer.selectedCustomer!.soldTo;

  const url = '/order-search';
  const params = { 'sold-to': soldTo };

  dispatch(
    apiCallBegan({
      url,
      params,
      onStart: beginOrderLoading.type,
      onSuccess: setOrderList.type,
      onError: resetOrderListOnError.type,
      showGlobalError: true,
    })
  );
};

export const fetchOrderDetails = (orderNumber: string): AppThunk => async (dispatch, getState) => {
  const soldTo = getState().customer.selectedCustomer!.soldTo;

  const url = `/order-detail/${orderNumber}`;
  const params = { 'sold-to': soldTo };

  dispatch(orderSlice.actions.setSelectedSalesOrder({ orderNumber }));

  dispatch(
    apiCallBegan({
      url,
      params,
      onStart: beginOrderDetailLoading.type,
      onSuccess: setOrderDetails.type,
      onError: stopOrderDetailLoading.type,
      showGlobalError: true,
    })
  );
};

export const selectOrderList = (state: RootState) => state.order.orderList;
export const selectOrderDetails = (state: RootState) => state.order.orderDetails;
export const selectOrder = (state: RootState) => state.order.selectedOrder;
export const selectOrderLoader = (state: RootState) => state.order.orderLoader;
export const selectOrderDetailLoader = (state: RootState) => state.order.orderDetailLoader;
